import {createTheme} from '@mui/material';
import {PaletteOptions, ThemeOptions} from "@mui/material/styles";

export const cssVar = (name: string) =>
    getComputedStyle(document.documentElement).getPropertyValue(name).trim();

// Headers the design draws on black. Buttons inside them flip to the light
// palette instead of carrying a surface prop -- the screen author then does not
// have to know which surface the button landed on.
const DARK_SURFACES = ['.MuiCardHeader-root', '.workplace-panel__head--dark'];
const ON_DARK_SURFACE = DARK_SURFACES.map((surface) => `${surface} &`).join(', ');

let themeOptionsFromVars = (): ThemeOptions => ({
    palette: {
        primary: {
            main: cssVar('--color-primary'),
            contrastText: cssVar('--color-primary-contrast'),
        },
        secondary: {
            main: cssVar('--color-secondary'),
        },
        success: {
            main: cssVar('--color-success'),
            contrastText: cssVar('--color-white'),
        },
        warning: {
            main: cssVar('--color-warning'),
            contrastText: cssVar('--color-white'),
        },
        error: {
            main: cssVar('--color-error'),
            contrastText: cssVar('--color-white'),
        },
        dark: {
            main: '#212121',
            light: '#595959',
            dark: '#595959',
            contrastText: '#fff',
        },
        background: {
            default: '#fff',
            paper: '#fff',
        },
    } as PaletteOptions,
    typography: {
        fontSize: 14,
        h5: {
            fontSize: 20,
        },
    },
});

// the overrides below never read the theme, so they merge onto the options -- passing
// a built Theme here makes TypeScript compare it to ThemeOptions, which on MUI 9 types
// takes over 2 GB and kills the dev server's type checker
export const createAppTheme = () => {
    return createTheme(themeOptionsFromVars(), {
        components: {
            MuiCard: {
                styleOverrides: {
                    root: {
                        '& .MuiCardHeader-root': {
                            backgroundColor: 'var(--color-black80)',
                            color: 'var(--color-white)',
                            borderBottomRightRadius: 0,
                            borderBottomLeftRadius: 0,
                        },
                        // the white text and outline of buttons in this header now
                        // come from the shared dark-surface rules (MuiButton,
                        // MuiIconButton below)
                        '& .MuiCardHeader-root .MuiButton-contained.MuiButton-colorInherit': {
                            color: 'var(--color-black80)',
                        },
                        '& .MuiCardHeader-root .MuiSvgIcon-root': {
                            marginRight: '5px',
                        },
                        '& .MuiCardContent-root': {
                            // borderTopRightRadius: 0,
                            // borderTopLeftRadius: 0,
                            padding: 0,
                        },
                        '& .MuiCardContent-root:last-child': {
                            paddingBottom: 0,
                        },
                        // the title is a flex row (see MuiCardHeader), so the spacing
                        // comes from its gap instead of per-icon margins
                        '& .MuiCardHeader-title > svg:first-of-type': {
                            color: 'var(--color-white50)',
                        },
                        '& .MuiCardHeader-title > .MuiSvgIcon-root': {
                            marginRight: 0
                        }
                    },
                }
            },
            MuiCardHeader: {
                styleOverrides: {
                    root: {
                        // the design draws a 64px toolbar; min-height holds it there
                        // even on pages whose header carries no back button. The 16px
                        // follows the AngularJS md-toolbar, not the 32px of the card
                        // content below -- matching the other half of the app wins
                        padding: '12px 16px',
                        minHeight: '64px',
                        boxSizing: 'border-box',
                        // the type matches the AngularJS md-toolbar so the black
                        // headers read the same on both halves of the app
                        // (Figma draws 16px Medium here -- deliberate deviation)
                        '& .MuiCardHeader-title': {
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            fontSize: '20px',
                            fontWeight: 400,
                            letterSpacing: '0.1px',
                        },
                        // cancels the icon button's own padding so the back arrow
                        // starts on the header padding, the way md-toolbar does it
                        '& .MuiCardHeader-title > .MuiIconButton-root:first-child': {
                            marginLeft: '-8px'
                        },
                    },
                    action: {
                        marginRight: 0,
                        // MUI pins the action to the top of the header; the design
                        // centres it against the title
                        alignSelf: 'center',
                        marginTop: 0,
                        marginBottom: 0,
                        // the AngularJS toolbar and Figma both put a 40px button here
                        '& .MuiButton-root': {
                            height: '40px'
                        }
                    }
                },
            },
            MuiList: {
                styleOverrides: {
                    root: {
                        '& .MuiButtonBase-root svg': {
                            color: 'rgba(0,0,0,0.54)',
                        },
                        '& .MuiButtonBase-root svg + span': {
                            marginLeft: '16px',
                        },
                    },
                },
            },
            MuiTable: {
                styleOverrides: {
                    root: {
                        '& .MuiTableCell-head': {
                            borderTop: '2px solid rgba(0,0,0,.12)',
                            '& > svg, & > div > svg': {
                                fontSize: '115%',
                                verticalAlign: 'text-top'
                            },
                            '& > svg:first-of-type:not(:last-child), & > div > svg:first-of-type:not(:last-child)': {
                                marginLeft: '-3px'
                            },
                            '& > svg + span, & > div > svg + span': {
                                marginLeft: '3px'
                            },
                            '& > span + svg, & > div > span + svg': {
                                marginLeft: '3px'
                            }
                        },
                        '& .MuiTableRow-root:last-child td': {
                            borderBottom: 'none',
                        },
                        '& .MuiTableCell-root svg': {
                            color: 'rgba(0,0,0,0.54)',
                        },
                        '& .MuiTableCell-head, & .MuiTableCell-body': {
                            padding: '6px 8px'
                        },
                        '& .MuiTableCell-body > .MuiIconButton-root': {
                            padding: '4px'
                        },
                        'a': {
                            color: 'var(--color-primary)',
                            textDecoration: 'underline',
                            '&:focus, &:hover': {
                                color: 'var(--color-primary60)'
                            }
                        },
                    },
                },
            },
            MuiButton: {
                styleOverrides: {
                    root: {
                        '&.MuiButton-contained.MuiButton-colorSuccess:disabled': {
                            backgroundColor: 'var(--color-success)',
                            color: 'var(--color-white)',
                            opacity: .6
                        },
                        [ON_DARK_SURFACE]: {
                            // only the primary action stays filled; everything else
                            // reads as white type on the black header
                            '&.MuiButton-outlined, &.MuiButton-text': {
                                color: 'var(--color-white)',
                            },
                            // MUI greys a disabled button towards black, which on a black
                            // header disappears. The design system keeps such a button on
                            // screen ("disabled, když nesmí TEĎ"), so the palette is
                            // inverted instead -- the same weights, mirrored onto white.
                            // TODO: swap for semantic/text-disabled-on-inverse once the
                            // Design System file is reachable.
                            '&.Mui-disabled': {
                                color: 'rgba(255, 255, 255, 0.5)',
                            },
                            '&.MuiButton-contained.Mui-disabled': {
                                backgroundColor: 'rgba(255, 255, 255, 0.12)',
                            },
                            '&.MuiButton-outlined.Mui-disabled': {
                                borderColor: 'rgba(255, 255, 255, 0.3)',
                            },
                            // MUI derives the outline from the text colour at half
                            // alpha, the design draws it solid
                            '&.MuiButton-outlined': {
                                borderColor: 'var(--color-white)',
                            },
                            // without this the hover border flips to the primary green
                            '&.MuiButton-outlined:hover': {
                                borderColor: 'var(--color-white)',
                                backgroundColor: 'var(--color-white15)',
                            },
                        },
                    }
                }
            },
            MuiIconButton: {
                styleOverrides: {
                    root: {
                        [ON_DARK_SURFACE]: {
                            color: 'var(--color-white)',
                            '&.Mui-disabled': {
                                color: 'rgba(255, 255, 255, 0.4)',
                            },
                        },
                    }
                }
            },
            MuiDialog: {
                styleOverrides: {
                    root: {
                        '& .MuiDialog-container': {
                            justifyContent: 'flex-center',
                            alignItems: 'flex-center',
                        },
                        '& .MuiPaper-root': {
                            width: '100%',
                            borderRadius: '16px',
                        },
                        '& .MuiDialogTitle-root': {
                            backgroundColor: 'var(--color-black80)',
                            color: 'var(--color-white)',
                        },
                        '& .MuiDialogActions-root': {
                            padding: '24px'
                        },
                        'a': {
                            color: 'var(--color-primary)',
                            textDecoration: 'underline',
                            '&:focus, &:hover': {
                                color: 'var(--color-primary60)'
                            }
                        },
                    },
                },
            },
            MuiSnackbar: {
                styleOverrides: {
                    root: {
                        '&.MuiSnackbar-root': {
                            position: 'static',
                            top: 'auto',
                        },
                        '&.MuiSnackbar-root + .MuiSnackbar-root': {
                            marginTop: '10px',
                        },
                    },
                },
            },
            MuiLinearProgress: {
                styleOverrides: {
                    root: {
                        flexShrink: 0,
                        '&.MuiLinearProgress-root[hidden]': {
                            background: 'transparent',
                        },
                        '&.MuiLinearProgress-root[hidden] > span': {
                            display: 'none',
                        },
                    },
                },
            },
            MuiFormLabel: {
                styleOverrides: {
                    root: {
                        '&': {
                            display: 'flex',
                            alignItems: 'center',
                            // main black, as Figma draws it. Focus deliberately keeps
                            // MUI's primary (Input/Focus is drawn green); the disabled
                            // rule below holds the black, so a form with read-only
                            // rows still reads as one column of labels
                            color: 'var(--color-black)',
                            '& > svg': {
                                paddingLeft: '4px',
                            },
                        },
                        '&.Mui-focused > .MuiFormLabel-asterisk': {
                            color: 'rgb(232,55,18)'
                        },
                        '&.Mui-disabled': {
                            color: 'var(--color-black)',
                        }
                    },
                },
            },
            MuiInput: {
                styleOverrides: {
                    root: {
                        '& input': {
                            // .cmd-input
                            padding: '2px 2px 1px',
                            lineHeight: '26px',
                            height: '30px',
                        },
                        '&:before': {
                            borderBottomColor: 'rgba(0,0,0,0.12)',
                        },
                    },
                },
            },
            MuiTooltip: {
                styleOverrides: {
                    tooltip: {
                        boxShadow: '0 0 12px 0 #0000001A',
                        background: 'var(--color-white)',
                        color: 'var(--color-black)',
                        fontSize: '14px',
                        lineHeight: '16px',
                        padding: '12px',
                        borderRadius: '8px',
                        fontWeight: 'normal',
                        whiteSpace: 'normal',
                        wordBreak: 'break-word',
                        maxWidth: '500px',
                        '& > div > strong': {
                            display: 'block',
                            padding: '10px 12px 8px 12px',
                            margin: '-12px -12px 12px -12px',
                            background: 'var(--color-black80)',
                            color: 'var(--color-white)',
                            borderRadius: '8px 8px 0 0'
                        }
                    },
                },
            },
            MuiAlert: {
                styleOverrides: {
                    root: {
                        // variants (not class selectors) keep the root-class specificity of the
                        // former standardSuccess/standardWarning keys, so e.g. toast styles still win
                        variants: [
                            {
                                props: {variant: 'standard', colorSeverity: 'success'},
                                style: {
                                    backgroundColor: 'var(--color-success-back)',
                                    color: 'var(--color-black)'
                                }
                            },
                            // {
                            //     props: {variant: 'standard', colorSeverity: 'error'},
                            //     style: {backgroundColor: 'var(--color-error-back)', color: 'white'}
                            // },
                            {
                                props: {variant: 'standard', colorSeverity: 'warning'},
                                style: {
                                    backgroundColor: 'var(--color-warning-back)',
                                    color: 'var(--color-black)'
                                }
                            },
                            // {
                            //     props: {variant: 'standard', colorSeverity: 'info'},
                            //     style: {backgroundColor: 'var(--color-info-back)', color: 'black'}
                            // },
                        ]
                    }
                }
            },
            MuiGrid: {
                styleOverrides: {
                    // the legacy Grid container filled its parent's width; Grid v2 does not,
                    // and inside a flex parent it shrank to its content
                    // (the container slot keeps root-class specificity, so an sx width still wins)
                    container: {
                        width: '100%',
                    },
                },
            },
            MuiStack: {
                styleOverrides: {
                    root: {
                        // MUI 5 left the first child's own margin alone, MUI 9 zeroes it with the
                        // spacing -- which pulled the first dense field of a form 8px up
                        '& > .MuiFormControl-marginDense:first-child': {
                            marginTop: '8px',
                            marginBottom: '4px',
                        },
                    },
                },
            },
            MuiTabs: {
                styleOverrides: {
                    root: {
                        borderBottom: '1px solid #D9D9D9'
                    }
                }
            },
            MuiTab: {
                styleOverrides: {
                    root: {
                        textTransform: 'unset',
                        // color: 'var(--color-black-text)',
                        color: 'rgba(0,0,0,0.54)',
                        fontWeight: '500',
                        fontSize: '14px',
                        lineHeight: '24px',
                        // primary text on primary15 fails WCAG contrast
                        '&.Mui-selected': {
                            color: 'var(--color-black)',
                            backgroundColor: 'var(--color-primary15)'
                        },
                        '&:hover': {
                            color: 'var(--color-black)',
                            backgroundColor: 'var(--color-primary15)'
                        }
                    }
                }
            }
        },
    });
}
